import axios from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Message } from 'semantic-ui-react'
import rosieLogo from '../../assets/rosie_logo_color.png'
import { setFluxLive, setFluxNoData } from '../../store/fluxLiveSlice'
import { useAppDispatch } from '../../store/hooks'
import { serverUrl } from '../../utilites/config'
import { reconnectingSocket } from '../../utilites/socket'
import Linechart from '../MainView/components/SolarFluxLive/components/Linechart'
import './EmbedFluxChart.scss'

type Band = 'f107' | 'f30'
type Range = 'live' | 'hourly' | 'daily'

const pad = (n: number) => String(n).padStart(2, '0')

const formatUtcForFilename = () => {
  const d = new Date()
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}_${pad(d.getUTCDate())}-${pad(d.getUTCMonth() + 1)}-${d.getUTCFullYear()}`
}

// Freshness of the data itself, so a stalled feed cannot look up to date
const formatUtcTimestamp = (d: Date) =>
  `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`

const BAND_API_PARAM: Record<Band, string> = { f107: '', f30: '?band=30' }
const BAND_LABELS: Record<Band, string> = { f107: 'F10.7', f30: 'F30' }
const RANGE_LABELS: Record<Range, string> = { live: '1s', hourly: '1h', daily: '1d' }
const RANGE_DISPLAY_LABELS: Record<Range, string> = { live: 'Live', hourly: 'Hourly avg', daily: 'Daily avg' }
const RANGE_API_PATH: Record<Range, string> = {
  live: '/flux/historicalData',
  hourly: '/flux/historicalHourlyData',
  daily: '/flux/historicalDailyData',
}
const LIVE_WS_TOPIC: Record<Band, string> = {
  f107: '/topic/flux-live',
  f30: '/topic/flux-live-30',
}

function EmbedFluxChart() {
  const [searchParams] = useSearchParams()
  const band = (searchParams.get('band') as Band) || 'f107'
  const range = (searchParams.get('range') as Range) || 'live'

  const isValidBand = band === 'f107' || band === 'f30'
  const isValidRange = range === 'live' || range === 'hourly' || range === 'daily'

  const [dataX, setDataX] = useState<Date[]>([])
  const [dataY, setDataY] = useState<number[]>([])
  const [hasActiveSession, setHasActiveSession] = useState(true)
  const connectionRef = useRef<any>(null)
  const dispatch = useAppDispatch()

  // Scope height resets to embed route only — avoids affecting main app layout
  useEffect(() => {
    document.documentElement.classList.add('embed-mode')
    return () => document.documentElement.classList.remove('embed-mode')
  }, [])

  const currentDateLabel = useMemo(() => {
    const d = new Date()
    return `${pad(d.getUTCDate())}-${pad(d.getUTCMonth() + 1)}-${d.getUTCFullYear()}`
  }, [])

  const lastUpdated = useMemo(() => {
    const latest = dataX[dataX.length - 1]
    return latest && !Number.isNaN(latest.getTime()) ? formatUtcTimestamp(latest) : null
  }, [dataX])

  const xaxisConfig = useMemo(() => {
    if (range === 'daily') return { tickformat: '%d %b', hoverformat: '%d-%m-%Y' }
    if (range === 'hourly') {
      return { title: { text: 'UTC' }, tickformat: '%H:%M', hoverformat: '%d-%m-%Y %H:%M' }
    }
    return { title: { text: `UTC — ${currentDateLabel}` } }
  }, [range, currentDateLabel])

  const yaxisConfig = useMemo(() => ({ title: { text: 'SFU' } }), [])

  const chartData = useMemo(
    () => [{ x: dataX, y: dataY, mode: 'lines', line: { color: 'black', width: 1 } }],
    [dataX, dataY],
  )

  useEffect(() => {
    if (!isValidBand || !isValidRange) return undefined

    let isActive = true
    setDataX([])
    setDataY([])

    const saveSocketData = (d: any) => {
      if (!isActive) return
      if (d.body === 'NO_DATA') {
        setHasActiveSession(false)
        dispatch(setFluxNoData())
        return
      }
      const parsed = JSON.parse(d.body)
      const latest = parsed[parsed.length - 1]
      setDataX(prev => [...prev.slice(1), new Date(latest.date)])
      setDataY(prev => [...prev.slice(1), parseFloat(latest.value)])
      setHasActiveSession(true)
      dispatch(setFluxLive())
    }

    const apiPath = RANGE_API_PATH[range] + BAND_API_PARAM[band]
    axios.get(serverUrl + apiPath).then(({ data }) => {
      if (!isActive) return
      const newX: Date[] = []
      const newY: number[] = []
      data.forEach((item: any) => {
        newX.push(new Date(item.date))
        newY.push(item.value)
      })
      setDataX(newX)
      setDataY(newY)
      setHasActiveSession(newX.length > 0)
    })

    if (range === 'live') {
      connectionRef.current = reconnectingSocket(
        serverUrl + '/websocket',
        saveSocketData,
        LIVE_WS_TOPIC[band],
        window.location.href,
      )
    }

    return () => {
      isActive = false
      connectionRef.current?.disconnect?.()
      connectionRef.current = null
      setDataX([])
      setDataY([])
    }
  }, [band, range, dispatch, isValidBand, isValidRange])

  if (!isValidBand || !isValidRange) {
    return (
      <div className='embed-flux-error'>
        <Message negative>
          <Message.Header>Invalid embed parameters</Message.Header>
          <p>Valid: band=f107|f30, range=live|hourly|daily</p>
        </Message>
      </div>
    )
  }

  return (
    <div className='embed-flux-root'>
      <div className='embed-flux-header'>
        <div className='embed-flux-header__brand'>
          <img src={rosieLogo} alt='ROSIE' className='embed-flux-header__logo' />
        </div>
        <div className='embed-flux-header__meta'>
          <span className='embed-flux-header__badge embed-flux-header__badge--band'>{BAND_LABELS[band]}</span>
          <span className='embed-flux-header__badge'>{RANGE_DISPLAY_LABELS[range]}</span>
        </div>
      </div>
      <div className='embed-flux-chart'>
        <Linechart
          data={chartData}
          xaxis={xaxisConfig}
          yaxis={yaxisConfig}
          downloadFilename={`[FLUX][${BAND_LABELS[band]}][${RANGE_LABELS[range]}]_${formatUtcForFilename()}`}
          hasActiveSession={hasActiveSession}
          uiRevisionKey={`embed-flux-${band}-${range}`}
          variant='embed'
        />
      </div>
      {lastUpdated && (
        <div className='embed-flux-footer'>Last updated: {lastUpdated} UTC</div>
      )}
    </div>
  )
}

export default EmbedFluxChart
